package com.devsusana.portfolio.components.sections

import androidx.compose.runtime.Composable
import com.devsusana.portfolio.components.widgets.TechBadge
import com.devsusana.portfolio.data.ExperienceData
import com.devsusana.portfolio.i18n.rememberStrings
import com.devsusana.portfolio.styles.SitePalette
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P

/**
 * Career timeline and technical competencies section.
 */
@Composable
fun ExperienceSection(modifier: Modifier = Modifier) {
    val colorMode = ColorMode.current
    val strings = rememberStrings()

    Column(
        modifier = modifier
            .fillMaxWidth()
            .id("experience")
            .styleModifier { property("scroll-margin-top", "5rem") }
            .padding(topBottom = 3.cssRem),
        verticalArrangement = Arrangement.spacedBy(2.cssRem)
    ) {
        // Section Header
        Column(verticalArrangement = Arrangement.spacedBy(0.5.cssRem)) {
            SpanText(
                text = strings.expEyebrow,
                modifier = Modifier
                    .color(SitePalette.androidGreen)
                    .fontSize(0.9.cssRem)
                    .fontWeight(FontWeight.Bold)
            )

            H2 {
                SpanText(
                    text = strings.expTitle,
                    modifier = Modifier
                        .color(SitePalette.textPrimary(colorMode))
                        .styleModifier { property("font-size", "clamp(1.4rem, 4.5vw, 2rem)") }
                        .fontWeight(FontWeight.Bold)
                )
            }

            P {
                SpanText(
                    text = strings.expSummary,
                    modifier = Modifier
                        .color(SitePalette.textSecondary(colorMode))
                        .fontSize(1.05.cssRem)
                        .lineHeight(1.6.cssRem)
                )
            }
        }

        // Skills Grid
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(0.75.cssRem)
        ) {
            H3 {
                SpanText(
                    text = strings.expCapabilitiesTitle,
                    modifier = Modifier
                        .color(SitePalette.textPrimary(colorMode))
                        .fontSize(1.2.cssRem)
                        .fontWeight(FontWeight.Bold)
                )
            }
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex-wrap", "wrap")
                        property("gap", "0.6rem")
                    },
                horizontalArrangement = Arrangement.Start
            ) {
                ExperienceData.skillsList.forEach { skill ->
                    TechBadge(
                        skill,
                        highlight = skill.contains("Android") || skill.contains("Compose") || skill.contains("Agentic")
                    )
                }
            }
        }

        // Milestones
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            strings.expMilestones.forEach { milestone ->
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .backgroundColor(SitePalette.surface(colorMode))
                        .borderRadius(12.px)
                        .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, SitePalette.surfaceBorder(colorMode))
                        .styleModifier {
                            property("padding", "clamp(1rem, 2.5vw, 1.5rem)")
                        }
                ) {
                    Column(
                        modifier = Modifier.fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(0.75.cssRem)
                    ) {
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .styleModifier {
                                    property("flex-wrap", "wrap")
                                    property("justify-content", "space-between")
                                    property("align-items", "flex-start")
                                    property("gap", "0.5rem")
                                }
                        ) {
                            Column(
                                modifier = Modifier.styleModifier {
                                    property("flex", "1 1 200px")
                                }
                            ) {
                                SpanText(
                                    text = milestone.role,
                                    modifier = Modifier
                                        .color(SitePalette.textPrimary(colorMode))
                                        .fontWeight(FontWeight.Bold)
                                        .fontSize(1.15.cssRem)
                                )
                                SpanText(
                                    text = milestone.company,
                                    modifier = Modifier
                                        .color(SitePalette.primaryAction(colorMode))
                                        .fontSize(0.95.cssRem)
                                        .fontWeight(FontWeight.Medium)
                                )
                            }
                            SpanText(
                                text = milestone.period,
                                modifier = Modifier
                                    .color(SitePalette.textSecondary(colorMode))
                                    .fontSize(0.85.cssRem)
                                    .styleModifier {
                                        property("white-space", "nowrap")
                                    }
                            )
                        }

                        SpanText(
                            text = milestone.summary,
                            modifier = Modifier
                                .color(SitePalette.textSecondary(colorMode))
                                .lineHeight(1.5.cssRem)
                        )

                        Column(verticalArrangement = Arrangement.spacedBy(0.35.cssRem)) {
                            milestone.highlights.forEach { highlight ->
                                SpanText(
                                    text = "• $highlight",
                                    modifier = Modifier
                                        .color(SitePalette.textSecondary(colorMode))
                                        .fontSize(0.9.cssRem)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
